import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";

// What can change on a tracked case: label, alert threshold, sharing. The court, date and number
// identify the case in the docket, so they stay fixed (delete and add again to change them).
export default function EditCaseDialog({ open, onClose, c, groups, onSaved }) {
  const [f, setF] = useState({ label: "", threshold: 5, group_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && c) setF({ label: c.label || "", threshold: c.threshold || 5, group_id: c.group_id || "" });
  }, [open, c]);

  if (!open || !c) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/cases/${c.id}`, { label: f.label, threshold: f.threshold, group_id: f.group_id });
      onSaved(data);
      toast.success("Οι αλλαγές αποθηκεύτηκαν");
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid="edit-case-dialog"
      style={{ background: "rgba(245,245,247,0.6)", backdropFilter: "saturate(180%) blur(20px)", WebkitBackdropFilter: "saturate(180%) blur(20px)" }} onClick={onClose}>
      <div className="card w-full max-w-md p-6 fadeup" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-2xl font-semibold tracking-tight">Επεξεργασία υπόθεσης</h3>
          <button onClick={onClose} className="btn-ghost w-9 h-9 grid place-items-center"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-[var(--txt-3)] mb-5 font-mono">{c.case_number} · {c.court_name}</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label block mb-1.5">Ετικέτα</label>
            <input data-testid="edit-label" value={f.label} maxLength={120} onChange={(e) => setF({ ...f, label: e.target.value })}
              className="field w-full px-3 py-2.5 text-sm" placeholder="π.χ. Υπόθεση Παπαδόπουλου" />
          </div>
          <div>
            <label className="label block mb-1.5">Ειδοποίηση όταν απομένουν</label>
            <select data-testid="edit-threshold" className="field w-full px-3 py-2.5 text-sm" value={f.threshold}
              onChange={(e) => setF({ ...f, threshold: parseInt(e.target.value) })}>
              {[...new Set([1, 2, 3, 5, 8, 10, f.threshold])].sort((a, b) => a - b).map((n) => <option key={n} value={n}>{n} υποθέσεις</option>)}
            </select>
          </div>
          <div>
            <label className="label block mb-1.5">Διαμοιρασμός σε ομάδα</label>
            <select data-testid="edit-group" className="field w-full px-3 py-2.5 text-sm" value={f.group_id}
              onChange={(e) => setF({ ...f, group_id: e.target.value })}>
              <option value="">Μόνο εγώ</option>
              {(groups || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5">Άκυρο</button>
            <button data-testid="edit-save" disabled={saving} className="btn-primary px-6 py-2.5 flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}Αποθήκευση
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
